import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { get, isEmpty, isArray } from 'lodash';
import { TimeoutWrapperService, BreadCrumbService } from '@nrwl/common-lib';
import {
  LogicalOperator,
  Operator,
  SubmitEvent,
  AdvancedSearchService,
  Expression
} from '@nrwl/advanced-search';
import { INavigationItem, NavigationProvider } from '@nrwl/navigation-abstract';
import { IMonitor, FILTER_KEYS, TestTypeValue } from '../../entities/types';
import { Monitor } from '../../entities/monitor.model.factory';
import { MonitorsService } from '../../service/monitors.service';
import * as config from '../../entities/config';

const dataConfig = require('../../json/monitorConfig.json');

@Component({
  selector: 'nrwl-monitors-page-widget',
  templateUrl: './monitors.page.widget.template.html',
  styleUrls: ['./monitors.page.widget.style.scss']
})
export class MonitorsPageWidgetComponent implements OnInit, OnDestroy {
  isDataAvailable: boolean;
  dataSource: BehaviorSubject<IMonitor[]>;
  listDataSource: Array<Monitor>;
  dataConfig: any; //eslint-disable-line
  cacheObj: Subscription;
  title: string;
  filterQuery: Expression[] = null;
  layeredSource = [];
  public advancedSearch = {
    valueSubmit: 'Update',
    info: { body: config.advancedSearchInfoBody },
    allowedOperators: [Operator.Equal],
    allowedLogicalOperators: [LogicalOperator.AND],
    allowedKeywords: [
      { keyword: FILTER_KEYS.TEST_NAME },
      { keyword: FILTER_KEYS.TEST_TYPE },
      { keyword: FILTER_KEYS.STATUS },
      { keyword: FILTER_KEYS.APP },
      { keyword: FILTER_KEYS.SITE },
      { keyword: FILTER_KEYS.DEVICE }
    ],
    allowGrouping: false,
    minimumFilters: 0,
    limitAutocomplete: 5,
    useOnce: [],
    emitInitialExpression: true
  };
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly monitorsService: MonitorsService,
    private readonly timeout: TimeoutWrapperService,
    private readonly navigator: NavigationProvider,
    private readonly advancedSearchService: AdvancedSearchService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadCrumbService: BreadCrumbService
  ) {
    this.dataSource = new BehaviorSubject<IMonitor[]>([]);
  }

  ngOnInit(): void {
    this.dataConfig = dataConfig;
    this.handleQueryParams();
    this.isDataAvailable = false;
    if (this.dataConfig) {
      this.title = get(this.dataConfig, 'title', '');
    }
    this.cacheObj = this.monitorsService.cache.subscribe(data => {
      this.listDataSource = this.monitorsService.sortDataByName(data);
      this.applySearchFilter();
    });
    this._setBreadCrumbs();
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  handleQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
      let advancedFilters = params['advanced_search'];
      advancedFilters = advancedFilters ? JSON.parse(advancedFilters) : advancedFilters;
      this.filterQuery = this.advancedSearchService.extractKeywordsValuesObjects(advancedFilters);
      this.advancedSearchService.addFilters(this.filterQuery, true, false);
      this.applySearchFilter();
      this.layeredSource = isArray(params.layeredSource)
        ? params.layeredSource
        : params.layeredSource
        ? [params.layeredSource]
        : params.layeredSource;
      this._setBreadCrumbs();
    });
  }

  //eslint-disable-next-line
  public advancedSearchFiltersApplied(event: SubmitEvent): void {
    this.advancedSearchService.isLoading = false;
    if (!event.isFirstEvent) {
      this.filterQuery = this.advancedSearchService.extractKeywordsValuesObjects(
        event.expressions.withValues
      );
      this.applySearchFilter();
      this.saveAdvancedSearchFiltersToURL(event.expressions.withValues);
    }
  }

  applySearchFilter(): void {
    const filteredData = this.applyFreeFormFilters();
    this.dataSourceChanged(filteredData);
  }

  /**
   * Save advanced_search in case someone wants to copy/paste this url
   */

  saveAdvancedSearchFiltersToURL(expression: string): void {
    this.navigator
      .addRootParam('advanced_search', expression)
      .navigateToDashboard('monitor-management', {
        replaceUrl: true
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  dataSourceChanged(dataSource: any): void {
    this.dataSource.next(dataSource);
  }

  applyFreeFormFilters(): Monitor[] {
    const dataSource = this.listDataSource;
    if (isEmpty(this.filterQuery)) {
      return dataSource;
    }
    const testTypeQuery = this.filterQuery.find(query => query.keyword === 'testtype');
    let isNetworkType = false;
    if (testTypeQuery) {
      isNetworkType = testTypeQuery.value === 'network';
    }
    return dataSource?.filter(data => {
      const monitorData = data.getMonitor();
      return this.filterQuery.every(filter => {
        const filteredValue = filter.value?.toLowerCase();
        const filterConfig = Object.assign({}, config.FilterConfig[filter.keyword]);
        if (filterConfig) {
          if (isNetworkType && filter.keyword === 'device') {
            filterConfig.fieldNames = filterConfig.fieldNames.slice(0, 1);
          }
          if (filter.operator === Operator.Equal) {
            return filterConfig.fieldNames.some(fieldName => {
              return filterConfig.source === 'enum'
                ? get(monitorData, fieldName)?.toLowerCase().includes(filteredValue)
                : get(monitorData, fieldName)?.toLowerCase() === filteredValue;
            });
          }
          if (filter.operator === Operator.NotEqual) {
            return !filterConfig.fieldNames.some(fieldName => {
              return filterConfig.source === 'enum'
                ? get(monitorData, fieldName)?.toLowerCase().includes(filteredValue)
                : get(monitorData, fieldName)?.toLowerCase() === filteredValue;
            });
          }
        }
      });
    });
  }

  hasData(status: boolean): void {
    this.timeout.set(
      'checkDataAvailable',
      () => {
        this.isDataAvailable = status;
      },
      0
    );
  }

  private _setBreadCrumbs(): void {
    const breadCrumbValues: Array<INavigationItem> = [];
    if (this.layeredSource?.length) {
      this.layeredSource.forEach(breadCrumbLabel => {
        breadCrumbValues.push({ label: breadCrumbLabel });
      });
    }
    breadCrumbValues.push({
      label: 'Test list'
    });
    this.breadCrumbService.setBreadCrumbs(breadCrumbValues);
  }
}
